import { Injectable, effect, signal } from '@angular/core';

export type AppTheme = 'white' | 'black' | 'green';

const STORAGE_KEY = 'nibni_theme';

function isAppTheme(value: unknown): value is AppTheme {
    return value === 'white' || value === 'black' || value === 'green';
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
    private readonly _theme = signal<AppTheme>('white');
    readonly theme = this._theme.asReadonly();

    constructor() {
        const initial = this.readStoredTheme() ?? this.readThemeFromDom() ?? 'white';
        this._theme.set(initial);

        effect(() => {
            const theme = this._theme();
            this.applyToDom(theme);
            this.storeTheme(theme);
        });
    }

    setTheme(theme: AppTheme): void {
        this._theme.set(theme);
    }

    private readStoredTheme(): AppTheme | null {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return isAppTheme(raw) ? raw : null;
        } catch {
            return null;
        }
    }

    private storeTheme(theme: AppTheme): void {
        try {
            localStorage.setItem(STORAGE_KEY, theme);
        } catch {
            // Ignore storage failures (private mode, blocked storage, etc).
        }
    }

    private readThemeFromDom(): AppTheme | null {
        if (typeof document === 'undefined') return null;
        const value = document.documentElement?.dataset?.['theme'];
        return isAppTheme(value) ? value : null;
    }

    private applyToDom(theme: AppTheme): void {
        if (typeof document === 'undefined') return;
        document.documentElement.dataset['theme'] = theme;
    }
}
